import { useEffect, useRef, useState } from "react";

import {
  barnLabel, fetchAdminHighlights, fetchSaleHighlights, isNew, money, postTime,
  setHighlightStatus, signedMoney, type SaleHighlight,
} from "@/lib/rbSaleHighlights";

import { useViewerIsAdmin } from "@/hooks/useViewerIsAdmin";

/**
 * Sale Highlights — the homepage's right sidebar (owner, 2026-10-14, from the
 * mock: "Ok lets go live with this"). Standout lots, posted by the database
 * the minute they qualify, newest first, scrolling slowly upward and stopping
 * under a finger or a pointer.
 *
 * Polls every 30 s while the tab is visible, paired with the 30 s TTL on
 * rb_sale_highlights_feed in /api/market. A post that arrives flashes gold.
 *
 * Admins (Rob, the owner) see held and hidden posts too, with Hide / Post.
 */
const POLL_MS = 30_000;
const SPEED = 0.028; // px per ms — a slow drift, readable while it moves

export function SaleHighlights() {
  const isAdmin = useViewerIsAdmin();
  const [posts, setPosts] = useState<SaleHighlight[] | null>(null);
  const [fresh, setFresh] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const seen = useRef<Set<number> | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const paused = useRef(false);
  // Re-read the clock each minute so a NEW tag drops off on time.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  // ── Load and poll ─────────────────────────────────────────────────────────
  useEffect(() => {
    let live = true;
    const load = async () => {
      const next = isAdmin ? await fetchAdminHighlights() : await fetchSaleHighlights();
      if (!live || !next) return;
      const ids = new Set(next.map((p) => p.id));
      if (seen.current) {
        const added = next.filter((p) => !seen.current!.has(p.id)).map((p) => p.id);
        if (added.length) {
          setFresh(new Set(added));
          if (box.current) box.current.scrollTop = 0;
          setNow(Date.now());
          window.setTimeout(() => { if (live) setFresh(new Set()); }, 6000);
        }
      }
      seen.current = ids;
      setPosts((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    void load();
    const t = window.setInterval(() => { if (document.visibilityState === "visible") void load(); }, POLL_MS);
    const onShow = () => { if (document.visibilityState === "visible") void load(); };
    document.addEventListener("visibilitychange", onShow);
    return () => { live = false; window.clearInterval(t); document.removeEventListener("visibilitychange", onShow); };
  }, [isAdmin]);

  // ── The drift ─────────────────────────────────────────────────────────────
  // A second copy of the list sits under the first so the loop has no seam.
  // Off for reduced motion and for admins (who need the rows to hold still).
  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || isAdmin || !posts?.length) return;
    let raf = 0, last = 0, y = box.current?.scrollTop ?? 0;
    const step = (ts: number) => {
      const dt = last ? Math.min(64, ts - last) : 0;
      last = ts;
      const el = box.current, h = list.current?.offsetHeight ?? 0;
      if (el && h > el.clientHeight) {
        if (paused.current) y = el.scrollTop;
        else {
          y += dt * SPEED;
          if (y >= h) y -= h;
          el.scrollTop = y;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [posts, isAdmin]);

  const hold = () => { paused.current = true; };
  const release = () => { paused.current = false; };
  const releaseLater = () => { window.setTimeout(release, 2500); };

  const act = async (p: SaleHighlight, status: "live" | "hidden") => {
    setBusy(p.id); setNote(null);
    const err = await setHighlightStatus(p.id, status);
    setBusy(null);
    if (err) { setNote(err); return; }
    setPosts((prev) => prev?.map((x) => (x.id === p.id ? { ...x, status } : x)) ?? prev);
    setNote(status === "live" ? "Posted. It is on the homepage within a minute."
      : p.status === "held" ? "Hidden. It will not post." : "Hidden. It is off the homepage within a minute.");
  };

  // Admins: posts waiting on a decision first, then everything by time.
  const visible = (posts ?? [])
    .filter((p) => isAdmin ? p.status !== "replaced" && p.status !== "withdrawn" : true)
    .sort((a, b) => isAdmin ? Number(b.status === "held") - Number(a.status === "held") : 0);
  const waiting = isAdmin ? visible.filter((p) => p.status === "held").length : 0;
  const liveCount = visible.filter((p) => !p.status || p.status === "live").length;
  const loop = !isAdmin && visible.length > 3;

  const row = (p: SaleHighlight, copy: boolean) => (
    <li key={(copy ? "c" : "") + p.id}
        className={"rbtx-hl-item" + (isNew(p.lotAt, now) ? " is-recent" : "") + (fresh.has(p.id) && !copy ? " is-arriving" : "")}
        data-status={isAdmin ? p.status : undefined}>
      <div className="rbtx-hl-meta">
        {isNew(p.lotAt, now) && <span className="rbtx-hl-new">New</span>}
        <span>{postTime(p)}</span>
        <span className="rbtx-hl-tag" data-k={p.kind}>{p.kind}</span>
        {isAdmin && p.status && p.status !== "live" && <span className="rbtx-hl-state">{p.status}</span>}
      </div>
      <p className="rbtx-hl-line">
        <b>{barnLabel(p)}</b> reports {p.head} {p.cattleType} ({p.weight} lbs) @ <span className="rbtx-hl-px">{money(p.price)}</span>
      </p>
      <p className="rbtx-hl-why"><b>{signedMoney(p.premium)}</b> vs last week</p>
      {isAdmin && !copy && (
        <div className="rbtx-hl-admin">
          {p.statusReason && p.status !== "live" && <span className="rbtx-hl-reason">{p.statusReason}</span>}
          {/* Held: Rob decides — Post puts it up, Hide dismisses it for good
              (owner, 2026-10-14: "we need to build out how he dismisses it").
              Hidden: Post undoes a Hide. Live: Hide takes it down. */}
          <span className="rbtx-hl-btns">
            {(p.status === "held" || p.status === "hidden") &&
              <button type="button" disabled={busy === p.id} onClick={() => void act(p, "live")}>Post</button>}
            {(p.status === "held" || p.status === "live") &&
              <button type="button" className="is-quiet" disabled={busy === p.id} onClick={() => void act(p, "hidden")}>Hide</button>}
          </span>
        </div>
      )}
    </li>
  );

  return (
    <section className="rbtx-hl" aria-label="Sale highlights">
      <div className="rbtx-hl-head">
        <span className="rbtx-raillabel">Sale Highlights</span>
        {liveCount > 0 && <span className="rbtx-hl-live"><i aria-hidden="true" />{liveCount} posted</span>}
        {waiting > 0 && <span className="rbtx-hl-waiting">{waiting} waiting for you</span>}
      </div>
      <div className="rbtx-hl-panel">
        <div className="rbtx-hl-scroll" ref={box} tabIndex={0} aria-label="Sale highlights, newest first"
             onMouseEnter={hold} onMouseLeave={release} onFocus={hold} onBlur={release}
             onTouchStart={hold} onTouchEnd={releaseLater}>
          {posts === null
            ? <p className="rbtx-hl-empty">Loading today's highlights…</p>
            : visible.length === 0
              ? <p className="rbtx-hl-empty">Highlights post here as standout lots sell.</p>
              : <>
                  <ul className="rbtx-hl-list" ref={list}>{visible.map((p) => row(p, false))}</ul>
                  {loop && <ul className="rbtx-hl-list" aria-hidden="true">{visible.map((p) => row(p, true))}</ul>}
                </>}
        </div>
      </div>
      {isAdmin && note && <p className="rbtx-hl-note" role="status">{note}</p>}
    </section>
  );
}
